import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { verifyMFA, generateDeviceFingerprint, getDeviceName } from "../services/authService";
import { Footer } from "../components/layout/Footer";

export function MFAVerifyPage() {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mfaSessionId, mfaMethod, mfaPending, setAuth, clearMFAPending } =
    useAuthStore();

  // Get session from URL params or store
  const sessionId = searchParams.get("session_id") || mfaSessionId;
  const method = searchParams.get("method") || mfaMethod;

  useEffect(() => {
    // If no MFA session, redirect to login
    if (!sessionId && !mfaPending) {
      navigate("/login");
    }
  }, [sessionId, mfaPending, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate: 6 digits for OTP, 8 alphanumeric for backup code
    const isBackup = /[A-Za-z]/.test(code);
    if (isBackup && code.length !== 8) {
      setError("Backup codes are 8 characters");
      return;
    }
    if (!isBackup && code.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    if (!sessionId) {
      setError("Session expired. Please login again.");
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      // Generate fingerprint if trusting device
      let deviceFingerprint: string | undefined;
      let deviceName: string | undefined;
      if (trustDevice) {
        deviceFingerprint = await generateDeviceFingerprint();
        deviceName = getDeviceName();
      }

      const response = await verifyMFA(sessionId, code, trustDevice, deviceFingerprint, deviceName);
      console.log("MFA verify response:", response);

      if (!response.user) {
        console.error("No user in response:", response);
        setError("Authentication failed - no user data");
        return;
      }

      setAuth(
        response.access_token,
        response.refresh_token,
        response.expires_at,
        response.user,
      );
      clearMFAPending();

      // Small delay to ensure state is persisted before navigation
      setTimeout(() => {
        navigate("/search", { replace: true });
      }, 100);
    } catch (err) {
      console.error("MFA verify error:", err);
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    clearMFAPending();
    navigate("/login");
  };

  // Allow both digits and uppercase letters for backup codes
  const handleCodeChange = (value: string) => {
    // Allow alphanumeric, convert to uppercase, max 8 chars (backup code length)
    const cleaned = value
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 8);
    setCode(cleaned);
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Security info */}
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30">
              <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-surface-100">
                  Two-Factor Authentication
                </h3>
                <p className="text-sm text-surface-400">
                  {method === "totp"
                    ? "Enter the code from your authenticator app"
                    : "We sent a verification code to your email"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - MFA form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* MFA card */}
          <div className="bg-surface-900/50 backdrop-blur-lg rounded-2xl p-8 border border-surface-700/50 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-surface-100 mb-2">
                Verification Required
              </h2>
              <p className="text-surface-400">
                {method === "totp"
                  ? "Enter the 6-digit code from your authenticator app"
                  : "Enter the 6-digit code sent to your email"}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-sm text-red-400 text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Code input */}
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="000000 or BACKUP"
                  className="w-full px-4 py-4 bg-surface-800/50 border border-surface-700/50 rounded-xl text-surface-100 text-center text-2xl font-mono tracking-[0.3em] placeholder-surface-600 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                  autoFocus
                  maxLength={8}
                />
                <p className="text-xs text-surface-500 mt-2 text-center">
                  Enter 6-digit code or 8-character backup code
                </p>
              </div>

              {/* Trust device checkbox */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="w-5 h-5 rounded border-surface-600 bg-surface-800/50 text-primary-500 focus:ring-primary-500/50 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm text-surface-300 group-hover:text-surface-200 transition-colors">
                  Remember this device for 7 days
                </span>
              </label>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading || (code.length !== 6 && code.length !== 8)}
                className="w-full py-4 px-6 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl font-semibold transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg shadow-primary-500/25"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  "Verify & Continue"
                )}
              </button>
            </form>

            {/* Help text */}
            <div className="mt-6 text-center">
              <p className="text-surface-500 text-sm mb-4">
                {method === "totp"
                  ? "Can't access your authenticator? Use a backup code."
                  : "Didn't receive the code? Check your spam folder."}
              </p>
              <button
                onClick={handleCancel}
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}

export default MFAVerifyPage;
